import React, { useEffect, useRef, useState } from 'react';
import { withTranslation, useTranslation } from 'react-i18next';
import './Software.css';

/* ── Iconos para highlights ── */
const HIGHLIGHT_ICONS = {
  1: (color) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  ),
  2: (color) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 01-3.46 0"/>
    </svg>
  ),
  3: (color) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 20V10M12 20V4M6 20v-6"/>
    </svg>
  ),
  4: (color) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="2" width="14" height="20" rx="2"/>
      <path d="M12 18h.01"/>
    </svg>
  ),
};

const HIGHLIGHT_COLORS = ['#0ea5e9', '#f97316', '#8b5cf6', '#06b6d4'];

/* ── Highlight chip ── */
const HighlightChip = ({ index, data, t, visible }) => {
  const color = HIGHLIGHT_COLORS[index - 1] || HIGHLIGHT_COLORS[0];
  const renderIcon = HIGHLIGHT_ICONS[index];

  // Textos completamente desde el JSON — sin defaultValue
  const title = t(`${data}.highlight_${index}_title`);
  const desc  = t(`${data}.highlight_${index}_desc`);

  // Si la clave no existe o está vacía, i18next devuelve la propia clave.
  // Usamos una comprobación simple para no renderizar chips sin contenido real.
  const titleMissing = !title || title === `${data}.highlight_${index}_title`;
  if (titleMissing) return null;

  return (
    <div
      className="xAgro-sw-highlight"
      data-visible={visible}
      style={{ transitionDelay: `${index * 80 + 350}ms` }}
    >
      <div
        className="xAgro-sw-highlight-icon"
        style={{
          background: `${color}18`,
          borderColor: `${color}25`,
        }}
      >
        {renderIcon && renderIcon(color)}
      </div>
      <div className="xAgro-sw-highlight-body">
        <span className="xAgro-sw-highlight-title">{title}</span>
        {desc && desc !== `${data}.highlight_${index}_desc` && (
          <span className="xAgro-sw-highlight-desc">{desc}</span>
        )}
      </div>
    </div>
  );
};

/* ── Componente principal ── */
const Software = function Software({ data }) {
  const [t] = useTranslation('common');
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // ── Textos del componente, todos provenientes de common.json → xAgro.software ──
  const title      = t(`${data}.title`);
  const subtitle   = t(`${data}.subtitle`);
  const badge      = t(`${data}.badge`);
  const cta        = t(`${data}.cta`);
  const imgSrc     = t(`${data}.img`);
  const imgAlt     = t(`${data}.img-alt`);
  const imgCaption = t(`${data}.img_caption`);

  // Hay highlights si la primera clave existe y tiene valor real
  const firstHighlight = t(`${data}.highlight_1_title`);
  const hasHighlights  = firstHighlight && firstHighlight !== `${data}.highlight_1_title`;

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.hash = '#contact';
    }
  };

  return (
    <section
      id="xAgro-software"
      className="xAgro-sw-section"
      ref={sectionRef}
    >
      {/* Patrón de fondo */}
      <div className="xAgro-sw-bg-pattern" aria-hidden="true" />

      <div className="xAgro-sw-wrapper">
        <div className="row xAgro-sw-row">

          {/* ── Columna de contenido ── */}
          <div className="col-12 col-md-6 xAgro-sw-content">

            {/* Badge */}
            <div className="xAgro-sw-badge" data-visible={visible}>
              <span className="xAgro-sw-badge-dot" />
              {badge}
            </div>

            {/* Título */}
            <h1 className="xAgro-sw-title" data-visible={visible}>
              {title}
            </h1>

            {/* Subtítulo */}
            <p className="xAgro-sw-subtitle" data-visible={visible}>
              {subtitle}
            </p>

            {/* Highlights (si existen en el JSON) */}
            {hasHighlights && (
              <div className="xAgro-sw-highlights">
                {[1, 2, 3, 4].map((i) => (
                  <HighlightChip
                    key={i}
                    index={i}
                    data={data}
                    t={t}
                    visible={visible}
                  />
                ))}
              </div>
            )}

            {/* CTA */}
            <button
              className="xAgro-sw-cta"
              data-visible={visible}
              onClick={scrollToContact}
            >
              {cta}
              <svg
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* ── Columna de imagen ── */}
          <div className="col-12 col-md-6 xAgro-sw-image-col">
            <div className="xAgro-sw-image-container" data-visible={visible}>
              <div className="xAgro-sw-image-glow" />
              <img
                src={imgSrc}
                alt={imgAlt}
                className="xAgro-sw-image"
              />
              {imgCaption && (
                <p className="xAgro-sw-image-caption">{imgCaption}</p>
              )}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default withTranslation('common')(Software);